import { useEffect, useState } from "react";
import { EventsListFields  } from '../types';
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { setCurrentPage, setSearchText } from "../../../../redux/historicalSlice";
import { Button, Col, Row } from "react-bootstrap";
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import AlertMessage from "../../../../components/AlertMessage";
import Search from "../../../../components/Search";
import { deleteLogicEvent, endEvent, useEvents } from "../api/eventAPI";
import { SelectedFilter } from "../../../../components/Filters/types";
import AppTable, { ColumnConfig } from "../../../../components/AppTable/AppTable";
import { SectionPermissionTypeEnum } from "../../../../types/SectionPermissionTypesEnum";
import { useAlert } from "../../../../hooks/useAlert";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";
import { getEventModalEndDateSchema } from "../schema/eventSchema";
import { useModal } from "../../../../hooks/useModal";
import { formatDate } from "../../../../utils/formatters/DateFormatter";

export default function Event() {

    const { t } = useTranslation(["common","event"]);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const currentPage = useSelector((state: RootState) => state.historical.events.currentPage);
    const itemsPerPage = useSelector((state: RootState) => state.historical.events.itemsPerPage);
    const searchText = useSelector((state: RootState) => state.historical.events.searchText);

    const [searchTerm, setSearchTerm] = useState("");
    const [appliedFilters] = useState<SelectedFilter[]>([]);

    const { data: eventsPagination, isLoading, error, refetch } = useEvents({
        search: searchTerm,
        filters: appliedFilters,
    });

    useEffect(() => {
        if (location.state?.shouldRefresh) {
            refetch();
        }
    }, [location.state, refetch]);

    const handleClear = () => {
        dispatch(setCurrentPage({ table: "events", page: 1 }));
        dispatch(setSearchText({ table: "events", text: '' }));
        setSearchTerm("");
    }

    const handleSearchInput = () => {
        setSearchTerm(searchText || '');
        dispatch(setCurrentPage({ table: "events", page: 1 }));
    };

 
    const { hideAlert, alert, isVisible } = useAlert({ fromLocation: true });

    const columns: ColumnConfig<EventsListFields>[] = [
        { header: t("event:headerTableEventId"), key: "id", render: (item) => <b>{item.id}</b> },
        { header: t("event:headerTableEventDescription"), key: "description" },
        { header: t("event:headerTableEventDateInicial"), key: "startDate", render: (item) => <>{formatDate(item.startDate)}</> },
        { header: t("event:headerTableEventDateFinal"), key: "endDate", render: (item) => <>{formatDate(item.endDate)}</> },
    ];

    const { showModal, modalData, endDate, currentId, openModal,
        closeModal,
        setEndDate,
        errorModal,
        setErrorModal,
        currentItem
    } = useModal<EventsListFields>();


    const confirmSubmitModal = async () => {

        try {
            if (modalData.type === 'deleteWithoutInput') {

                await deleteLogicEvent(currentId);
                navigate('/admin/event', {
                    state: { message: t("event:modal.eventDeleteSuccessMissage"), shouldRefresh: true }
                });
            } else if (modalData.type === 'end' && endDate) {
                const schema = getEventModalEndDateSchema(t, currentItem?.startDate ?? null);
                try {
                    await schema.validate({ endDate }, { abortEarly: false });

                } catch (err: any) {
                    const validationErrors: { [key: string]: string | undefined } = {};
                    err.inner?.forEach((error: any) => {
                        if (error.path) {
                            validationErrors[error.path] = error.message;
                        }
                    });
                    setErrorModal(validationErrors);
                    return;
                }

                await endEvent(currentId, endDate);
                navigate('/admin/event', {
                    state: { message: t("eevent:modal.eventEndSuccessMissage"), shouldRefresh: true }
                });
            }
            closeModal();
        } catch (error) {
            setErrorModal({ errorModal: t("common:requestError.errorBadRequest") });
            console.log(error);
        }
    }


    return (
        <>
            <ModalGlobal
                show={showModal}
                onClose={closeModal}
                title={modalData.title}
                contentType={modalData.type}
                subtitle={modalData.subtitle}
                saveText={t("common:modalActionOK")}
                error={errorModal?.endDate || errorModal?.errorModal}
                endDate={endDate}
                onEndDateChange={(date: Date | null) => setEndDate(date)}
                onSave={confirmSubmitModal}
                isLoading={isLoading}
            />
            <Row className="mb-2 align-items-center justify-content-between">
                <Col xs={12} md>
                    <h2>{t("event:titleEvent")}</h2>
                </Col>
                <Col xs={12} md="auto" className="d-flex align-items-center justify-content-end mt-2 mt-md-0">
                    <Button
                        variant="primary"
                        style={{ whiteSpace: "nowrap" }}
                        onClick={() => navigate(`/admin/event/new-event`)}>
                        {t("event:buttonNewEvent")}
                    </Button>
                </Col>
            </Row>
            <Row>
                <Col className="text-center" xs={12} md={12}>
                    {isVisible && (
                        <AlertMessage
                            message={alert!.message}
                            type={alert!.type}
                            onClose={hideAlert}
                        />
                    )}
                </Col>
                <Col className="text-center" xs={12} md={12}>
                    {error && (
                        <div className="alert alert-danger">{t("common:requestError.errorBadRequest")}</div>
                    )}
                </Col>
            </Row>
            <Row className="mb-2">
                <Col xs={12}>
                    <Search
                        searchText={searchText || ''}
                        onChange={(text) => dispatch(setSearchText({ table: "events", text: text }))}
                        onSearch={handleSearchInput}
                        onClear={handleClear}
                    />
                </Col>
            </Row>

            <AppTable<EventsListFields>
                data={eventsPagination?.items || []}
                columns={columns}
                emptyMessage={"No records found"}
                t={t}
                hasActions={true}
                hasPagination={true}
                currentPage={currentPage}
                totalPages={eventsPagination?.totalPages || 0}
                pageSize={itemsPerPage}
                paginationType="events"
                onExportCsv={() => { }}
                onDelete={(item) => { openModal(item, t("event:modal.modalDeleteEventTitle"), `${t("event:modal.modalDeleteEventTitle")} ${item.description}?`, "deleteWithoutInput", "") }}
                onEnd={(item) => { openModal(item, t("event:modal.eventModalDataTitle"), t("event:modal.eventModalDataSubtitle"), "end", "")  }}
                onView={(id: number) => { navigate(`/admin/event/${id}`) }}
                sectionPermission={SectionPermissionTypeEnum.EVENTS}
            />
        </>
    );
}
